/**
 * Phase 1d — Entity-held family home: Age Pension principal-home exemption and aged care cap.
 * Ported verbatim from frontend/src/calculations/entityHomeRules.js.
 */
import type { FactFind } from './factFindTypes.ts';
import type { StrategyOptions } from './strategyOptionsTypes.ts';
import { partnerRemainsInFamilyHome } from './householdStatus.ts';

/** Client and/or partner still treat as living in own home (occupancy). */
export function occupantHasOwnHomeResidency(factFind: FactFind): boolean {
    const isPartnered = factFind?.adviceFor === 'couple';
    if (factFind?.client?.currentResidency === 'own_home') return true;
    return isPartnered && factFind?.partner?.currentResidency === 'own_home';
}

/** Trust/company on title + security of tenure + occupant → principal home exempt from pension entity pool. */
export function isEntityPrincipalHomeExempt(factFind: FactFind, strategyOptions: StrategyOptions = {}): boolean {
    const home = factFind?.home || {};
    const homeValue = home.value || 0;
    if (homeValue <= 0) return false;
    if (home.ownership !== 'other_entity' || !home.entityId || home.entityId === '__unsure__') {
        return false;
    }
    if (strategyOptions.rentHome || strategyOptions.downsizeHome) return false;
    if (home.securityOfTenure === false) return false;
    return occupantHasOwnHomeResidency(factFind);
}

export function isEntityHeldStep3Home(factFind: FactFind): boolean {
    const home = factFind?.home || {};
    return (
        (home.value || 0) > 0 &&
        home.ownership === 'other_entity' &&
        !!home.entityId &&
        home.entityId !== '__unsure__'
    );
}

/**
 * The `home.protectedPerson` answers that actually confer the exemption.
 *
 * An allow-list, not "anything except 'no'". The field also carries answers that describe the home's
 * occupancy WITHOUT conferring anything — `occupied_not_protected` is someone living there who fails
 * the protected-person test, an adult child in work being the usual case, and `will_rent_out` is a
 * home let to a tenant. Under the old test those would have exempted the home simply by not being the
 * string 'no'. Being an allow-list is what lets new occupancy answers be added without touching a
 * single fee: anything not named here is assessed.
 */
const AGED_CARE_PROTECTED_PERSONS: ReadonlySet<string> = new Set(['partner', 'dependent_child', 'carer', 'close_relative']);

/**
 * Protected person still in home → aged care excludes home value (SA485 / CLIK 4.4.1).
 *
 * TWO ways to be one, and the second is not a question anybody is asked:
 *
 *   1. The fact-find says so — a dependent child, a long-term carer or close relative, recorded on
 *      `home.protectedPerson` because only the client knows about them.
 *   2. A partner stays in the home while the other enters care. Nobody needs to answer that: the
 *      care goal already says who is going in, and each person's living arrangement says where the
 *      other one is. See `partnerRemainsInFamilyHome`.
 *
 * The derived case OVERRIDES a stored `'no'`, deliberately. `'no'` is the field's default value, so
 * an untouched form and a considered "nobody will be there" are indistinguishable in the data — and
 * the form does not even ask when a partner is staying (ClientProfileSteps.tsx). Treating the default
 * as an answer is what assessed the home for every one-partner-into-care couple.
 *
 * Single source of truth on purpose: this rule was inlined in three places (this module, the aged
 * care costs engine and the costs-explained screen), so the fee and the explanation of the fee could
 * disagree. They all call this now.
 */
export function hasAgedCareProtectedPerson(factFind: FactFind, strategyOptions: StrategyOptions = {}): boolean {
    const home = factFind?.home || {};
    // `factFind.protectedPerson` is the pre-`home` location, still read for files written back then
    // and typed only as an index-signature value — hence the String().
    const protectedPersonValue = String(home?.protectedPerson ?? factFind?.protectedPerson ?? '');
    if (AGED_CARE_PROTECTED_PERSONS.has(protectedPersonValue)) return true;
    return partnerRemainsInFamilyHome(factFind, strategyOptions);
}

/** Assessable home value for residential aged care MTA when title is entity-held. */
export function getEntityHomeAssessableForAgedCare(factFind: FactFind, strategyOptions: StrategyOptions = {}, homeCap: number): number | null {
    if (!isEntityHeldStep3Home(factFind)) return null;
    const homeValue = factFind.home?.value || 0;
    if (strategyOptions.rentHome || strategyOptions.downsizeHome) {
        return Math.min(homeValue, homeCap);
    }
    if (hasAgedCareProtectedPerson(factFind, strategyOptions)) return 0;
    return Math.min(homeValue, homeCap);
}
